package com.arenaproplay.admin.domain.admin

import com.arenaproplay.admin.domain.me.ChatListPage
import com.arenaproplay.admin.domain.me.ChatMessagesPage

interface AdminRepository {
    suspend fun getOverviewKpis(period: Period): OverviewKpis
    suspend fun getUserGrowth(period: Period): CategorizedTimeSeries
    suspend fun getUsersByPlatform(period: Period): List<CategoryShare>
    suspend fun getSportPopularity(period: Period): List<CategoryShare>
    suspend fun getRecentActivity(limit: Int = 10): List<ActivityItem>

    suspend fun getSubscriptionKpis(period: Period): SubscriptionKpis

    // --- Analytics baseline ("reset analytics" without deleting data) ---

    /** ISO instant all analytics count from, or null = full history. */
    suspend fun getAnalyticsBaseline(): String?

    /** Starts counting analytics from now; returns the new baseline instant. */
    suspend fun resetAnalyticsBaseline(): String

    /** Removes the baseline so analytics cover the full history again. */
    suspend fun clearAnalyticsBaseline()

    suspend fun getUsers(page: Int = 0, pageSize: Int = 25, query: String = ""): PageResult<UserRow>

    /** «Пипл»: только полные профили (имя+аватар+др+интересы), новые по активности сверху. */
    suspend fun getPeople(page: Int = 0, pageSize: Int = 24): PageResult<AdminPerson>

    /** Deletes a user so the same email can register again from scratch. */
    suspend fun deleteUser(userId: String)

    /** Gives the user a long-lived PRO subscription (KPI-safe, audited). */
    suspend fun grantPro(userId: String)

    /** Shuts down the user's current subscription (a store sub re-syncs on next app launch). */
    suspend fun revokePro(userId: String)

    /** Все фото юзера (профильное + аватары карточек) для диалога модерации. */
    suspend fun getUserPhotos(userId: String): UserPhotos

    /**
     * Точечно удаляет ОДНО фото пользователя по причине модерации.
     * [reason] — одно из "NO_PERSON" | "INAPPROPRIATE".
     * [target] — "PROFILE" (только профильное фото) или "CARD" (карточка
     * [cardId] удаляется целиком вместе с фото).
     * Возвращает true при успехе; выбрасывает исключение при ошибке (409 = фото нет).
     */
    suspend fun removeUserAvatar(userId: String, reason: String, target: String, cardId: String? = null): Boolean

    suspend fun getEvents(page: Int = 0, pageSize: Int = 25, query: String = ""): PageResult<EventRow>

    // ── Event management (/events/{id}) ─────────────────────────────────────

    /** Полная карточка ивента: статус, участники (PENDING/CONFIRMED), команды со счётом. */
    suspend fun getEventDetail(eventId: String): AdminEventDetail

    /** PLAYER-стат-типы спорта + текущие статы игроков (shared, не admin-префикс). */
    suspend fun getEventStats(eventId: String): AdminEventStats

    suspend fun confirmEventParticipant(eventId: String, userId: String)

    suspend fun rejectEventParticipant(eventId: String, userId: String)

    /** IN_PROGRESS | COMPLETED | CANCELLED (переходы валидирует бэкенд). */
    suspend fun setEventStatus(eventId: String, status: String)

    suspend fun setEventTeamScore(eventId: String, teamId: String, score: Int)

    /** Абсолютные значения по statTypeId (PRO-ивент, только IN_PROGRESS). */
    suspend fun setEventPlayerStats(eventId: String, userId: String, stats: List<EventStatEntryPayload>)

    /** Сохраняет конфигурацию ивента как шаблон; возвращает сохранённый шаблон. */
    suspend fun saveEventTemplate(eventId: String, name: String): AdminEventTemplate

    /** Список всех сохранённых шаблонов ивентов. */
    suspend fun getEventTemplates(): List<AdminEventTemplate>

    /** Удаляет шаблон ивента по id. */
    suspend fun deleteEventTemplate(templateId: String)

    /** Шаблон + дата → готовый ивент от имени сохранённого организатора, с командами и составами. */
    suspend fun createEventFromTemplate(templateId: String, dateTime: String, endDateTime: String? = null): CreateEventFromTemplateResult

    /** Создаёт ивент от аккаунта админа через юзерский POST /v1/events; ответ — карточка нового ивента. */
    suspend fun createEvent(payload: CreateEventPayload): AdminEventDetail

    // ── Event teams + composition ───────────────────────────────────────────

    /** Поиск юзеров по username/имени (для добавления игроков в команды). */
    suspend fun searchUsers(query: String): List<AdminUserSearchResult>

    /** Создаёт ad-hoc команду в ивенте (юзерский POST /v1/events/{id}/teams, ADMIN-bypass). */
    suspend fun createEventTeam(eventId: String, name: String, color: String? = null): AdminEventTeam

    suspend fun deleteEventTeam(eventId: String, teamId: String)

    /** Добавляет юзера CONFIRMED-участником (upsert), опционально сразу в команду. */
    suspend fun addEventParticipant(eventId: String, userId: String, eventTeamId: String? = null): AdminEventParticipant

    suspend fun assignParticipantTeam(eventId: String, userId: String, eventTeamId: String)

    suspend fun removeParticipantFromTeam(eventId: String, userId: String)

    /** Воссоздаёт команды+составы из шаблона на свежесозданном ивенте. */
    suspend fun applyEventTemplate(eventId: String, templateId: String): ApplyTemplateResult

    /** Browses all clubs on the platform (page is 0-based, backend caps size at 200). */
    suspend fun getClubs(page: Int = 0, pageSize: Int = 25): ClubListPage

    suspend fun createClub(request: CreateClubRequest): ClubRow

    /** Club with gallery and rules included. */
    suspend fun getClub(clubId: String): ClubRow

    suspend fun updateClub(clubId: String, request: UpdateClubRequest): ClubRow

    /** Permanently deletes a club and its dependent data (fields, schedule, …). */
    suspend fun deleteClub(clubId: String)

    /** Uploads an image for a club (banner or gallery) and returns its public URL. */
    suspend fun uploadClubImage(bytes: ByteArray, fileName: String, contentType: String): String

    /** Загружает картинку для новости app-launch (jpeg/png/webp) → публичный URL. */
    suspend fun uploadAppLaunchImage(bytes: ByteArray, fileName: String, contentType: String): String

    /** Replaces the whole gallery with [imageUrls] (order defines sortOrder). */
    suspend fun updateClubGallery(clubId: String, imageUrls: List<String>): List<ClubGalleryImage>

    /** Replaces all club rules with [rules] (order defines sortOrder). */
    suspend fun updateClubRules(clubId: String, rules: List<String>): List<ClubRule>

    suspend fun getClubSchedule(clubId: String): ClubSchedule

    /** Replaces the weekly working hours (dayOfWeek is ISO: 1 = Monday … 7 = Sunday). */
    suspend fun updateClubWorkingHours(clubId: String, entries: List<WorkingHoursEntry>)

    suspend fun getClubAmenities(clubId: String): List<String>
    suspend fun updateClubAmenities(clubId: String, amenities: List<String>): List<String>

    suspend fun getClubServices(clubId: String): List<ClubService>
    suspend fun createClubService(clubId: String, request: CreateClubServiceRequest): ClubService
    suspend fun updateClubService(
        clubId: String,
        serviceId: String,
        request: UpdateClubServiceRequest,
    ): ClubService
    suspend fun deleteClubService(clubId: String, serviceId: String)

    /** Sports available as a club type. */
    suspend fun getSports(): List<SportOption>

    /** Address suggestions via the backend Google Places proxy (query must be ≥ 2 chars). */
    suspend fun autocompleteAddress(query: String, session: String?): List<AddressSuggestion>

    /** Resolves a picked suggestion to coordinates; closes the billing [session]. */
    suspend fun getPlaceAddress(placeId: String, session: String?): PlaceAddress

    /** Subscription purchases, newest first (who bought and when). */
    suspend fun getSubscriptions(
        page: Int = 0,
        pageSize: Int = 25,
        query: String = "",
        status: SubscriptionStatusFilter = SubscriptionStatusFilter.ALL,
    ): PageResult<SubscriptionRow>

    suspend fun getApiRequests(period: Period): List<ApiRequestStats>
    suspend fun getApiRequestsByPlatform(period: Period): List<ApiPlatformBreakdown>
    suspend fun getApiErrors(period: Period): List<ApiErrorRow>

    suspend fun getAuditEntries(limit: Int = 50, offset: Long = 0): List<AuditEntry>

    /** Support inbox — messages users send from the web contact form and the mobile support sheet. */
    suspend fun getSupportMessages(limit: Int = 50, offset: Long = 0): SupportMessagesPage

    // Launch popup (announcements + version gate)
    suspend fun getAppAnnouncements(): List<AppAnnouncementRow>

    suspend fun createAppAnnouncement(payload: SaveAppAnnouncementPayload): AppAnnouncementRow

    suspend fun updateAppAnnouncement(id: String, payload: SaveAppAnnouncementPayload): AppAnnouncementRow

    suspend fun deleteAppAnnouncement(id: String)

    suspend fun getAppUpdatePolicy(): AppUpdatePolicyMap

    suspend fun setAppUpdatePolicy(policy: AppUpdatePolicyMap)

    /** Every chat on the platform — read-only moderation view (page is 1-based). */
    suspend fun getAllChats(page: Int = 1, pageSize: Int = 25, query: String = ""): ChatListPage

    suspend fun getActiveStories(page: Int = 1, pageSize: Int = 30): AdminStoriesPage

    /** Read-only chat history, newest first; page is 1-based (page 1 = latest). */
    suspend fun getChatMessages(chatId: String, page: Int = 1, pageSize: Int = 50): ChatMessagesPage

    suspend fun deleteStory(storyId: String)

    /** [authorType] is USER or CLUB — matches the feed post's authorType. */
    suspend fun deletePost(postId: String, authorType: String)

    suspend fun getSportEventSizes(): List<SportEventSize>

    suspend fun getRetentionCohorts(weeks: Int = 12): List<RetentionCohort>
    suspend fun getSignupFunnel(): List<FunnelStep>
    suspend fun getEventsByCity(limit: Int = 20): List<CityEventCount>

    suspend fun getEventLifecycle(): EventLifecycleStats
    suspend fun getOnboardingCompletion(): OnboardingStats

    suspend fun getOnlineNow(): OnlineNow

    suspend fun getBehaviorKpis(period: Period): BehaviorKpis
    suspend fun getBehaviorActiveUsersSeries(period: Period): CategorizedTimeSeries
    suspend fun getBehaviorEventsSeries(period: Period): CategorizedTimeSeries
    suspend fun getBehaviorTopScreens(period: Period, limit: Int = 20): List<TopScreenRow>
    suspend fun getBehaviorTopActions(period: Period, limit: Int = 20): List<TopActionRow>
    suspend fun getBehaviorCountries(period: Period, limit: Int = 30): List<CountryUsageRow>
    suspend fun getBehaviorDevices(period: Period): List<DeviceShareRow>
    suspend fun getBehaviorFirstSteps(period: Period, steps: Int = 5, limit: Int = 20): List<JourneyPathRow>
    suspend fun getNewUserFunnel(period: Period): NewUserFunnel
    suspend fun getNewUserActions(period: Period, limit: Int = 15): List<TopActionRow>
    suspend fun getUserTimeline(userId: String, limit: Int = 200): List<TimelineEvent>

    /**
     * Paginated list of behaviour sessions in [period].
     * [limit] is coerced to [1, 200] by the backend; default 25 gives one visible page.
     */
    /** Живая гео-диагностика: что сервер знает об IP админа и провайдерах. */
    suspend fun getGeoDebug(): GeoDebug

    suspend fun getSessions(period: Period, limit: Int = 25, offset: Int = 0): SessionList

    /** Chronological timeline of all events that happened inside [sessionId] (capped at 500). */
    suspend fun getSessionEvents(sessionId: String): List<TimelineEvent>

    // --- Content Studio (seeding fake content) ---

    /** Creates [SeedUsersRequest.count] fully-fleshed fake users (pro-subscribed, marked is_test). */
    suspend fun seedUsers(request: SeedUsersRequest): List<SeedUserRow>

    /** Creates ONE fake user with hand-filled fields; blanks are generated. */
    suspend fun seedCustomUser(request: SeedCustomUserRequest): SeedUserRow

    /** Edits a fake user; blank fields keep their current values. */
    suspend fun updateSeedUser(userId: String, request: SeedCustomUserRequest): SeedUserRow

    /** One hand-written post on behalf of a fake user; returns the post id. */
    suspend fun seedCustomPost(request: SeedCustomPostRequest): String

    /** One story (24h, single image) on behalf of a fake user; returns the story id. */
    suspend fun seedCustomStory(request: SeedCustomStoryRequest): String

    /** Uploads a story image (as the admin) and returns its public URL. */
    suspend fun uploadStoryImage(bytes: ByteArray, fileName: String, contentType: String): String

    /** Participants of a seeded event (creator included). */
    suspend fun getSeedEventParticipants(eventId: String): List<SeedParticipant>

    /** Uploads an event cover image (as the admin) and returns its public URL. */
    suspend fun uploadEventImage(bytes: ByteArray, fileName: String, contentType: String): String

    suspend fun getSeedUsers(page: Int = 0, pageSize: Int = 25): PageResult<SeedUserRow>

    /** Card templates available for AI avatar generation (defaults + premium). */
    suspend fun getSeedCardTemplates(): List<SeedCardTemplate>

    /** Runs the AI avatar pipeline for a fake user on the chosen card; ~30–120s. */
    suspend fun generateSeedAvatar(
        userId: String,
        cardTemplateId: String,
        bytes: ByteArray,
        fileName: String,
        contentType: String,
        prompt: String? = null,
    ): SeedAvatarResult

    /** Returns the number of posts created. */
    suspend fun seedPosts(request: SeedPostsRequest): Int

    /** Uploads a post image (as the admin) and returns its public URL for [seedPosts]. */
    suspend fun uploadPostImage(bytes: ByteArray, fileName: String, contentType: String): String

    suspend fun seedEvent(request: SeedEventRequest): SeedEventResult

    /** Events created by fake users. */
    suspend fun getSeedEvents(page: Int = 0, pageSize: Int = 25): PageResult<EventRow>

    /** Returns the number of participants actually added. */
    suspend fun addSeedParticipants(eventId: String, request: SeedParticipantsRequest): Int

    suspend fun removeSeedParticipant(eventId: String, userId: String)

    suspend fun updateSeedEventStatus(eventId: String, status: String): SeedEventStatusResult

    suspend fun deleteSeedEvent(eventId: String)

    /** Deletes ALL fake users and their content. */
    suspend fun purgeSeedData(): SeedPurgeResult

    // --- Acquisition analytics ---

    suspend fun getAcquisitionSummary(period: Period): AcqSummaryDto
    suspend fun getAcquisitionSources(period: Period): List<AcqSourceDto>
    suspend fun getAcquisitionGeo(period: Period): AcqGeoDto
    suspend fun getAcquisitionFunnel(period: Period): AcqFunnelDto
    suspend fun getAcquisitionInterests(period: Period, source: String? = null): List<AcqInterestDto>

    // --- Paywall analytics ---

    suspend fun getPaywallFunnel(period: Period, platform: String? = null): PaywallFunnelDto

    // ── Arena News moderation ──

    suspend fun getNewsArticles(status: String?, page: Int = 1, pageSize: Int = 20): NewsArticlePage

    suspend fun updateNewsArticle(id: String, payload: SaveNewsArticlePayload): NewsArticleRow

    /** DRAFT | PUBLISHED | ARCHIVED. Publishing also triggers the AI translation
     *  pass; [notify]=true additionally broadcasts a localized push to everyone. */
    suspend fun setNewsStatus(id: String, status: String, notify: Boolean = false): NewsArticleRow

    /** Слот 1..10 в топе «Самые обсуждаемые»; null убирает. */
    suspend fun setNewsFeaturedRank(id: String, rank: Int?): NewsArticleRow

    /** Кандидаты для привязки: матчи вокруг даты (±1 день), q — по командам. */
    suspend fun searchNewsMatches(sport: String, date: String, query: String): List<NewsMatchLink>

    /** Привязывает (null — отвязывает) матч к статье. */
    suspend fun setNewsMatchLink(id: String, match: NewsMatchLink?): NewsArticleRow

    /** Список «Обсуждаемое» — статьи со слотом, слот 1 первым. */
    suspend fun getFeaturedNews(): List<NewsArticleRow>

    /** Добавляет статью в конец списка «Обсуждаемое» (следующий свободный слот). */
    suspend fun appendFeaturedNews(id: String): NewsArticleRow

    /** Пересобирает список «Обсуждаемое»: ids в порядке показа; не вошедшие убираются. */
    suspend fun reorderFeaturedNews(ids: List<String>): List<NewsArticleRow>

    suspend fun deleteNewsArticle(id: String)

    suspend fun getNewsImageCandidates(id: String): NewsImageCandidates

    suspend fun uploadNewsImage(bytes: ByteArray, fileName: String, contentType: String): String

    /** Manual RSS ingestion pass — files fresh drafts immediately. */
    suspend fun runNewsIngest(): NewsIngestReport

    /** Queues background AI re-scoring of all DRAFT articles' importance. */
    suspend fun runNewsRescore(): NewsRescoreReport
}
